import React, { createContext, useContext, useMemo } from 'react';
import { useField } from 'react-final-form';

import { isContainerBlock, type TBlock } from '@wp-block-builder/package';

type TBlockState = {
  name: string;
  block: TBlock;
  ancestors: TBlock[];
};
type TState = Record<string, TBlockState>;
const Context = createContext<TState>({});

type Props = {
  children: React.ReactNode;
};
export default function BlocksMapStore({ children }: Props) {
  const blocks = useField<TBlock[]>('configuration.blocks').input.value;
  const value = useMemo<TState>(
    () =>
      populateDictionary({}, blocks, {
        name: 'configuration.blocks',
        ancestors: [],
      }),
    [blocks]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useBlockAncestors(id: string): TBlock[] {
  return useContext(Context)[id].ancestors;
}

export function useBlockParent(id: null | string): TBlock | null {
  const context = useContext(Context);
  if (id === null) {
    return null;
  }
  const ancestors = context[id].ancestors;
  return ancestors.at(-1) ?? null;
}

export const useBlockName = (id: null | string) => {
  const c = useContext(Context);
  if (id === null) {
    return null;
  }
  return c[id].name;
};

export const useBlock = (id: null | string) => {
  const c = useContext(Context);
  if (id === null) {
    return null;
  }
  return c[id].block;
};

type TAncestorInformation = {
  name: string;
  ancestors: TBlock[];
};
function populateDictionary(
  dictionary: Record<string, TBlockState>,
  blocks: (TBlock | null)[],
  { name, ancestors }: TAncestorInformation
): Record<string, TBlockState> {
  blocks.forEach((block, i) => {
    if (block === null) {
      return;
    }
    dictionary[block.id] = {
      block,
      name: `${name}[${i}]`,
      ancestors: [...ancestors],
    };
    if (isContainerBlock(block)) {
      populateDictionary(dictionary, block.props?.blocks ?? [], {
        name: `${name}[${i}].props.blocks`,
        ancestors: [...ancestors, block],
      });
    }
  });
  return dictionary;
}
